'use client'

import {
  Smartphone,
  CreditCard,
  Users,
  TrendingUp,
  Wifi,
  ShoppingCart,
} from 'lucide-react'

/**
 * Philippines UX Design Diagrams
 * Visual components for the Philippines UX mastery blog
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

// 1. Philippine Digital Landscape — key stats
export function PhilippineDigitalLandscapeDiagram() {
  const stats = [
    {
      value: '97.5M',
      label: 'internet users (83.8% penetration)',
      source: 'DataReportal, Jan 2025',
      color: '#008dcc',
      icon: Users,
    },
    {
      value: '88%',
      label: 'of web traffic from mobile devices',
      source: 'DataReportal, Dec 2024',
      color: '#F90B8A',
      icon: Smartphone,
    },
    {
      value: '57.4%',
      label: 'of retail payments are now digital',
      source: 'BSP, 2024',
      color: '#FDC304',
      icon: CreditCard,
    },
    {
      value: '8.5%',
      label: 'of GDP from the digital economy',
      source: 'GMA/PSA, 2024',
      color: '#8b5cf6',
      icon: TrendingUp,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE PHILIPPINE DIGITAL LANDSCAPE
        </h3>
        <p className='text-sm text-muted-foreground'>
          Why UX design for the Philippine market demands a mobile-first,
          locally-adapted approach
        </p>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        {stats.map((stat, idx) => {
          const Icon = stat.icon
          return (
            <div
              key={idx}
              className='p-4 rounded-xl border text-center'
              style={{
                backgroundColor: `${stat.color}08`,
                borderColor: `${stat.color}30`,
              }}
            >
              <Icon
                className='w-5 h-5 mx-auto mb-2'
                style={{ color: stat.color }}
              />
              <p
                className='text-2xl md:text-3xl font-bold'
                style={{ color: stat.color }}
              >
                {stat.value}
              </p>
              <p className='text-[10px] text-muted-foreground mt-1 leading-tight'>
                {stat.label}
              </p>
              <p className='text-[9px] text-muted-foreground mt-1.5'>
                {stat.source}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// 2. Philippine Payment Ecosystem — GCash-dominated
export function PhilippinePaymentsDiagram() {
  const methods = [
    {
      name: 'GCash',
      share: 89,
      users: '94M registered / 81M active',
      detail: 'Dominates mobile wallet market',
      color: '#008dcc',
    },
    {
      name: 'Maya (PayMaya)',
      share: 5,
      users: '50M+ registered',
      detail: 'Growing credit card & banking',
      color: '#8b5cf6',
    },
    {
      name: 'Bank Transfer',
      share: 0,
      users: 'InstaPay +67.8% volume YoY',
      detail: 'QR Ph merchants +148.7% growth',
      color: '#FDC304',
    },
    {
      name: 'Cash / COD',
      share: 0,
      users: 'Still significant for e-commerce',
      detail: '7-Eleven, Bayad Centers',
      color: '#6b7280',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          PHILIPPINE PAYMENT ECOSYSTEM
        </h3>
        <p className='text-sm text-muted-foreground'>
          GCash dominance means your checkout UX must treat it as the primary
          payment method
        </p>
      </div>

      <div className='space-y-3'>
        {methods.map((method, idx) => (
          <div
            key={idx}
            className='p-4 rounded-xl border'
            style={{
              backgroundColor: `${method.color}05`,
              borderColor: `${method.color}25`,
            }}
          >
            <div className='flex items-center justify-between mb-2'>
              <div className='flex items-center gap-2'>
                <CreditCard
                  className='w-4 h-4'
                  style={{ color: method.color }}
                />
                <span className='text-sm font-bold text-foreground'>
                  {method.name}
                </span>
              </div>
              {method.share > 0 && (
                <span
                  className='text-xs font-bold px-2 py-0.5 rounded-full'
                  style={{
                    backgroundColor: `${method.color}15`,
                    color: method.color,
                  }}
                >
                  {method.share}% wallet share
                </span>
              )}
            </div>
            <div className='flex items-center justify-between'>
              <p className='text-xs text-foreground'>{method.users}</p>
              <p className='text-[10px] text-muted-foreground'>
                {method.detail}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-4 p-3 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20'>
        <div className='flex items-center gap-2'>
          <ShoppingCart className='w-4 h-4 text-[#008dcc]' />
          <p className='text-[10px] text-muted-foreground'>
            <strong className='text-foreground'>BSP target:</strong> 60-70%
            digital payments by 2028 — up from 57.4% today. Design for digital
            wallets as the default, not an alternative.
          </p>
        </div>
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-3'>
        Sources: BSP 2024, Thunes, Statista, Ken Research
      </p>
    </div>
  )
}

// 3. Mobile Usage Behavior — how Filipinos use the internet
export function FilipinoMobileBehaviorDiagram() {
  const behaviors = [
    {
      metric: 'Daily internet time',
      value: '8h 53m',
      context: 'Ages 16-64',
      bar: 92,
      color: '#008dcc',
    },
    {
      metric: 'Daily social media',
      value: '3h 32m',
      context: '4th highest globally',
      bar: 70,
      color: '#F90B8A',
    },
    {
      metric: 'Weekly digital media',
      value: '54 hours',
      context: '2nd highest globally',
      bar: 100,
      color: '#8b5cf6',
    },
    {
      metric: 'Social media users',
      value: '90.8M',
      context: '78% of population',
      bar: 78,
      color: '#FDC304',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          HOW FILIPINOS USE THE INTERNET
        </h3>
        <p className='text-sm text-muted-foreground'>
          Among the world&apos;s most engaged digital populations — design for
          attention, not just access
        </p>
      </div>

      <div className='space-y-3'>
        {behaviors.map((item, idx) => (
          <div key={idx} className='flex items-center gap-3'>
            <span className='text-xs text-muted-foreground w-28 text-right flex-shrink-0'>
              {item.metric}
            </span>
            <div className='flex-1 relative'>
              <div className='h-7 bg-muted rounded-full overflow-hidden'>
                <div
                  className='h-full rounded-full flex items-center justify-between px-3 transition-all'
                  style={{
                    width: `${item.bar}%`,
                    backgroundColor: `${item.color}20`,
                    borderRight: `3px solid ${item.color}`,
                  }}
                >
                  <span
                    className='text-xs font-bold'
                    style={{ color: item.color }}
                  >
                    {item.value}
                  </span>
                  <span className='text-[9px] text-muted-foreground hidden sm:inline'>
                    {item.context}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-4 p-3 rounded-lg bg-[#F90B8A]/5 border border-[#F90B8A]/20'>
        <div className='flex items-center gap-2'>
          <Wifi className='w-4 h-4 text-[#F90B8A]' />
          <p className='text-[10px] text-muted-foreground'>
            <strong className='text-foreground'>Design implication:</strong>{' '}
            Filipinos are extremely online but often on constrained bandwidth
            (32 Mbps avg mobile). Design for engagement AND performance.
          </p>
        </div>
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-3'>
        Sources: DataReportal Digital 2025, Campaign Asia, We Are Social
      </p>
    </div>
  )
}
